import { createContext, type ReactNode, useState } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
    parseTRA,
    TraBase,
    type TraV1,
    computeTraStatistics,
} from '@ics-portal/tra';
import { type ToastMessage } from '@ics-portal/guardian';
import { useToast } from '@contexts/GlobalUiContext';
import ErrorMessage from '@components/ErrorMessage';
import { type Tra, type TraQuery, type TraSetStateStatus } from '@gql/graphql';
import { GET_TRA, SET_TRA_STATE, UPDATE_TRA } from '@api/queries/tra';
import useUser from '@hooks/useUser';

export interface TRAContextValues<T> {
    loading: boolean;
    tra?: Tra;
    data?: T;
    updateTra: (partialTra?: Partial<Tra> & Partial<T>) => void;
    asyncUpdateTra: (
        partialTra?: Partial<Tra> & Partial<T>,
        toast?: ToastMessage,
    ) => Promise<void>;
    updateTraState: (state: TraSetStateStatus, comment?: string) => void;
    asyncUpdateTraState: (
        state: TraSetStateStatus,
        comment?: string,
    ) => Promise<void>;
}

const TRAsContext = createContext<TRAContextValues<unknown>>({
    loading: true,
    tra: undefined,
    data: new TraBase(
        { version: 0, schema: {}, phases: [], phaseLockRules: {} },
        () => {},
    ),
    updateTra: () => {},
    asyncUpdateTra: () => Promise.resolve(),
    updateTraState: () => {},
    asyncUpdateTraState: () => Promise.resolve(),
});

export function TRAProvider<T extends TraBase>({
    children,
}: {
    children: ReactNode;
}): ReactNode {
    const [change, setChange] = useState<string>();
    const [tra, setTra] = useState<Tra>();
    const { id } = useParams();
    const { pushSuccessToast, pushErrorToast } = useToast();
    const user = useUser();
    const navigate = useNavigate();
    const {
        loading: getLoading,
        error,
        data: traData,
    } = useQuery<TraQuery | null>(GET_TRA, {
        skip: !id,
        variables: { id: id ?? '' },
        fetchPolicy: 'network-only',
        onCompleted: (competeData) => {
            if (!competeData?.tras?.edges.length) {
                navigate('/page-not-found');
            }
        },
    });

    const [updateTraData, { loading: traUpdateLoading }] =
        useMutation(UPDATE_TRA);
    const [setTraState, { loading: traStateUpdateLoading }] = useMutation(
        SET_TRA_STATE,
        { refetchQueries: ['tra'] },
    );

    const loading = useMemo(
        () => getLoading || traUpdateLoading || traStateUpdateLoading,
        [getLoading, traUpdateLoading, traStateUpdateLoading],
    );

    const callback = useCallback(() => {
        setChange(window.crypto.randomUUID());
    }, [setChange]);

    useEffect(() => {
        const traContent: Tra | undefined = traData?.tras?.edges[0]?.node as
            | Tra
            | undefined;
        if (!traContent) {
            return;
        }
        setTra(traContent);
    }, [traData]);

    const data = useMemo(() => {
        if (!tra) {
            return;
        }
        const traBody = parseTRA(tra.body, callback);
        return traBody.data as unknown as T;
    }, [tra, callback]);

    const updateTra = useCallback(
        (partialTra?: Partial<Tra> & Partial<typeof data>): void => {
            asyncUpdateTra(partialTra).catch(() => {});
        },
        [asyncUpdateTra],
    );

    const updateTraState = useCallback(
        async (state: TraSetStateStatus): Promise<void> => {
            await asyncUpdateTraState(state);
        },
        [asyncUpdateTraState],
    );

    async function asyncUpdateTraState(
        state: TraSetStateStatus,
        comment?: string,
    ): Promise<void> {
        if (!tra) {
            return;
        }

        try {
            await setTraState({
                variables: {
                    id: tra.id,
                    state,
                    comment,
                },
            });
            pushSuccessToast({
                detail: 'TRA updated',
            });
        } catch (e) {
            const detail =
                e instanceof Error ? e.message : 'Error updating TRA';

            pushErrorToast({
                detail,
            });
            throw e;
        }
    }

    async function asyncUpdateTra(
        partialTra?: Partial<Tra> & Partial<typeof data>,
        toast?: ToastMessage,
    ): Promise<void> {
        if (!tra) {
            return;
        }
        if (tra.lockedBy && user && tra.lockedBy.id !== user.id) {
            return;
        }
        data?.updateData<typeof data>(partialTra ?? {});

        const body = JSON.stringify(data);
        const metadata = data
            ? JSON.stringify(computeTraStatistics(data as unknown as TraV1))
            : undefined;

        try {
            const result = await updateTraData({
                variables: {
                    input: {
                        id: tra.id,
                        approver1:
                            partialTra?.approver1?.id ?? tra.approver1?.id,
                        approver2:
                            partialTra?.approver2?.id ?? tra.approver2?.id,
                        description: partialTra?.description ?? tra.description,
                        title: partialTra?.title ?? tra.title,
                        body,
                        metadata,
                    },
                },
            });
            const updatedTra = result.data?.updateTRA;

            if (updatedTra) {
                setTra({ ...updatedTra, body, metadata } as Tra);
            }
            if (toast) {
                if (toast.severity === 'error') {
                    pushErrorToast({
                        detail: toast.detail ?? 'TRA updated',
                    });
                } else if (toast.severity === 'success') {
                    pushSuccessToast({
                        detail: toast.detail ?? 'TRA updated',
                    });
                } else {
                    pushSuccessToast({
                        detail: 'TRA updated',
                    });
                }
            } else {
                pushSuccessToast({
                    detail: 'TRA updated',
                });
            }
        } catch (e) {
            const detail =
                e instanceof Error ? e.message : 'Error updating TRA';

            pushErrorToast({
                detail,
            });
            throw e;
        }
    }

    const value = useMemo(
        () => ({
            loading,
            tra,
            data,
            updateTra,
            asyncUpdateTra,
            updateTraState,
            asyncUpdateTraState,
        }),
        [
            loading,
            tra,
            data,
            updateTra,
            asyncUpdateTra,
            updateTraState,
            asyncUpdateTraState,
        ],
    );

    if (error) {
        return <ErrorMessage error={error} />;
    }

    return (
        <TRAsContext.Provider value={value}>{children}</TRAsContext.Provider>
    );
}

export default TRAsContext;
